"use client";

import { GTMEvent } from ".constants/gtmEvents";
import { cn, OfferNoTimer, Offer as UIOffer } from "@cloai/uikit";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { ElementType, FC, memo } from "react";

import { useABtestContext } from "@/providers";

import { Timer } from "./Timer";
import { useClickOffer } from "./useClickOffer";

type OfferProps = {
  gtmEvent?: string | null;
  description: string;
  endsAt: Date;
  href?: string | null;
  position: number;
  timerColor?: string | null;
  title: string;
  type: OfferType | number;
  url: string | null;
  textColor?: string | null;
};

enum OfferType {
  WITH_TIMER = 0,
  NO_TIMER = 1,
}

enum TextColor {
  BLACK = "black",
  WHITE = "white",
}

type TimerColorType = "orange" | "white" | "pink" | undefined;

export const Offer: FC<OfferProps> = memo(
  ({
    type,
    href,
    url,
    endsAt,
    title,
    description,
    timerColor,
    gtmEvent,
    textColor,
  }: OfferProps) => {
    const n = useTranslations("TimerForOffer");
    const { AAB_RedesignV1 } = useABtestContext();

    const handleClickOffer = useClickOffer();

    const isBannerWithTimer = type === OfferType.WITH_TIMER && endsAt;

    const textColorCn = () => {
      if (textColor === TextColor.BLACK) {
        return "text-black";
      }

      if (textColor === TextColor.WHITE) {
        return "text-white";
      }

      return "text-black";
    };

    if (AAB_RedesignV1) {
      const Component: ElementType = href ? Link : "div";
      const linkProps = href ? { href, scroll: false } : {};
      const onClick = gtmEvent
        ? handleClickOffer(gtmEvent as GTMEvent)
        : undefined;

      return (
        <Component
          {...linkProps}
          className={cn(
            "group/offer relative block h-12 w-full",
            "md:h-14",
            onClick && "cursor-pointer",
          )}
          onClick={onClick}
        >
          <Image
            src={url || ""}
            alt=""
            width={430}
            height={48}
            priority
            quality={100}
            className="absolute block size-full object-cover"
          />
          <div
            className={cn(
              "relative flex size-full flex-col justify-evenly px-container transition-transform duration-100",
              "md:items-center",
              onClick && "group-active/offer:scale-95",
            )}
          >
            <span
              className={cn(
                "text-[clamp(0.75rem,min(4vw,4vh),1.125rem)] leading-none font-bold",
                textColorCn(),
              )}
            >
              {title}
            </span>

            {isBannerWithTimer ? (
              <Timer endsAt={endsAt} theme={timerColor as TimerColorType} />
            ) : (
              description && (
                <span
                  className={cn(
                    "text-[clamp(0.75rem,min(4vw,4vh),1.125rem)] leading-none font-medium",
                    textColorCn(),
                  )}
                >
                  {description}
                </span>
              )
            )}
          </div>
        </Component>
      );
    }

    return isBannerWithTimer ? (
      <UIOffer
        image={url || ""}
        theme="covered"
        title={<span className={textColorCn()}>{title}</span>}
        days={n("days")}
        hours={n("hours")}
        minutes={n("minutes")}
        seconds={n("seconds")}
        endDate={endsAt}
        href={href || undefined}
        onClick={gtmEvent ? handleClickOffer(gtmEvent as GTMEvent) : undefined}
        timerColor={(timerColor || undefined) as TimerColorType}
      />
    ) : (
      <OfferNoTimer
        image={url || ""}
        theme="covered"
        title={<span className={textColorCn()}>{title}</span>}
        text={
          <span className={cn("text-sm/4 font-medium", textColorCn())}>
            {description}
          </span>
        }
        href={href || undefined}
        onClick={gtmEvent ? handleClickOffer(gtmEvent as GTMEvent) : undefined}
      />
    );
  },
);
Offer.displayName = "Offer";
